import { Input } from "@/components/ui/input";
import React, {
  ChangeEvent,
  FocusEvent,
  InputHTMLAttributes,
  useEffect,
  useRef,
} from "react";

type Props = {
  value?: string | number;
  onChange: (value: string) => void;
};

// Removes any character that's not a number or decimal point
const stripNonFloatChars = (val: string) => val.replace(/[^0-9.]/g, "");

// Formats a number string to comma serpared currency to two decimal places
const formatCurrency = (val: string) => {
  if (val === "") return val;
  return Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(val));
};

// Formats a number string to a comma separated number string
const formatDisplayDollars = (dollars: string) =>
  Intl.NumberFormat("en-US").format(Number(dollars));

// Formats number string to maximum of two digits
const formatDisplayCents = (cents: string) =>
  cents.slice(0, Math.min(cents.length, 2));

// Formats a number string to comma separated currency value for input to render
//    1000.00 -> 1,000.00
//    100. -> 100.
//    100.1 -> 100.1
//    100.123 -> 100.12
const formatDisplayCurrency = (val: string) => {
  if (val === "") return val;
  if (val.includes(".")) {
    const [dollars, cents] = val.split(".");

    // Even if the value of cents is a empty string, we still need to render the decimal point
    // to allow users to type the decimanl and cents value
    return `${formatDisplayDollars(dollars)}.${formatDisplayCents(cents)}`;
  }
  return formatDisplayDollars(val);
};

// This component abstracts the logic to display a currency so that forms can treat
// the value a number
export const CurrencyInput = React.forwardRef<
  HTMLInputElement,
  Omit<InputHTMLAttributes<HTMLInputElement>, "onChange"> & Props
>(({ value = "", onChange, ...props }, ref) => {
  const prevValue = useRef("");
  if (typeof value === "number") {
    value = `${value}`;
  }

  const updateValue = (val: string) => {
    const formattedValue = stripNonFloatChars(val);
    prevValue.current = formattedValue;
    onChange(formattedValue);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    updateValue(e.target.value);
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement, Element>) => {
    if (value != "") {
      updateValue(formatCurrency(value));
    }
    props.onBlur?.(e);
  };

  useEffect(() => {
    if (prevValue.current !== value) {
      updateValue(formatCurrency(value));
    } else {
      prevValue.current = value;
    }
  });

  const displayValue =
    prevValue.current !== value
      ? formatCurrency(value)
      : formatDisplayCurrency(value);

  return (
    <Input
      ref={ref}
      {...props}
      placeholder="0.00"
      type="text"
      inputMode="decimal"
      adornment="$"
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
});
